import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';

const BUTTON_STYLE = {
  padding: '8px 24px',
  borderRadius: '8px',
  height: '40px',
};

/**
 * Confirms a synthesis that spends credits on audio the user already has:
 * re-creating audio for the whole project, or converting a hand-picked set of
 * blocks. Plain "create audio" — filling in blocks that have none — is not
 * confirmed, since there is nothing to lose there.
 *
 * `credits` is the total the run will consume, already summed by the caller.
 */
const PopupConfirmCreateAudio = ({ open, credits = 0, onClose, onConfirm }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  // The dialog fades out rather than disappearing, and its content stays
  // mounted for that fade. Closing it clears the pending blocks, so `credits`
  // collapses to 0 in the same commit that starts the fade — showing "0 điểm"
  // on the way out. Hold the last value seen while open so the number the user
  // agreed to (or declined) is the one still on screen as it goes.
  const lastCredits = useRef(credits);
  useEffect(() => {
    if (open) lastCredits.current = credits;
  }, [open, credits]);
  const shownCredits = open ? credits : lastCredits.current;

  return (
    <CommonDialog
      fullWidth
      width="512px"
      open={open}
      onClose={onClose}
      title={t('confirmCreateAudioTitle')}
      contentComponent={
        <Typography
          variant="body-16-regular"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('confirmCreateAudioDescription', {
              value: shownCredits?.toLocaleString(language),
            }),
          }}
        />
      }
      actionComponent={
        <>
          <CommonButton
            customStyle={BUTTON_STYLE}
            variant="outlineSmall"
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            onClick={onClose}
          >
            {t('cancel')}
          </CommonButton>
          <CommonButton
            customStyle={BUTTON_STYLE}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            variant="contained"
            onClick={onConfirm}
          >
            {t('continueConvert')}
          </CommonButton>
        </>
      }
    />
  );
};

export default PopupConfirmCreateAudio;
